// src/pages/public/AboutUs.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  TrendingUp, 
  Shield, 
  Users, 
  Globe, 
  Award, 
  Clock,
  Zap,
  BarChart3,
  Wallet,
  ArrowRight,
  Check
} from 'lucide-react'

const AboutUs = () => {
  const stats = [
    { value: '15,420+', label: 'Active Investors', icon: Users },
    { value: '$2.58M+', label: 'Total Invested', icon: TrendingUp },
    { value: '$1.95M+', label: 'Total Withdrawn', icon: Wallet },
    { value: '99.9%', label: 'Platform Uptime', icon: Shield }
  ]

  const values = [
    {
      icon: Shield,
      title: "Transparency",
      description: "Complete transparency in all operations with real-time tracking and blockchain verification",
      color: "blue"
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Constantly evolving with cutting-edge technology and AI-powered investment strategies",
      color: "green"
    },
    {
      icon: Users,
      title: "Community",
      description: "Building a strong community of investors with shared success and mutual growth",
      color: "purple"
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Commitment to delivering exceptional returns and unparalleled user experience",
      color: "orange"
    }
  ]

  const team = [
    {
      name: "Alex Johnson",
      role: "CEO & Founder",
      bio: "15+ years in fintech and blockchain technology",
      image: "/team/alex.jpg",
      social: { linkedin: "#", twitter: "#" }
    },
    {
      name: "Sarah Chen",
      role: "CTO",
      bio: "Former lead developer at major crypto exchange",
      image: "/team/sarah.jpg",
      social: { linkedin: "#", twitter: "#" }
    },
    {
      name: "Mike Rodriguez",
      role: "Investment Strategist",
      bio: "Wall Street veteran with 20+ years experience",
      image: "/team/mike.jpg",
      social: { linkedin: "#", twitter: "#" }
    },
    {
      name: "Emily Watson",
      role: "Security Director",
      bio: "Cybersecurity expert and blockchain specialist",
      image: "/team/emily.jpg",
      social: { linkedin: "#", twitter: "#" }
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              About{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                YesTimeEarn
              </span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              We're revolutionizing the investment landscape with AI-powered technology, 
              institutional-grade security, and transparent blockchain solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission & Vision
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 dark:bg-blue-900/20 p-3 rounded-2xl">
                    <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Democratizing Investments
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Making premium investment opportunities accessible to everyone, 
                      regardless of their financial background or experience level.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 dark:bg-green-900/20 p-3 rounded-2xl">
                    <Globe className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Global Financial Inclusion
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Creating a borderless investment platform that empowers individuals 
                      worldwide to achieve financial independence.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 dark:bg-purple-900/20 p-3 rounded-2xl">
                    <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      AI-Powered Growth
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Leveraging artificial intelligence to optimize returns and provide 
                      smart investment strategies for our users.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl"
            >
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              The principles that guide everything we do at YesTimeEarn
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105">
                  <div className={`bg-gradient-to-r from-${value.color}-500 to-${value.color}-600 w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Experienced professionals dedicated to your investment success
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105">
                  <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {member.name}
                  </h3>
                  <div className="text-blue-600 dark:text-blue-400 font-semibold mb-3">
                    {member.role}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    {member.bio}
                  </p>
                  {/* <div className="flex justify-center space-x-3">
                    <button className="text-gray-400 hover:text-blue-600 transition-colors">
                      <Globe className="w-5 h-5" />
                    </button>
                    <button className="text-gray-400 hover:text-blue-400 transition-colors">
                      <Users className="w-5 h-5" />
                    </button>
                  </div> */}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Advanced Technology Stack
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Powered by cutting-edge technology for maximum security and performance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: "Blockchain Security", description: "Immutable transaction records and smart contract verification" },
              { name: "AI Algorithms", description: "Machine learning for optimal investment strategies and risk management" },
              { name: "256-bit Encryption", description: "Military-grade security for all user data and transactions" },
              { name: "Real-time Analytics", description: "Live portfolio tracking and performance monitoring" },
              { name: "Multi-Currency Support", description: "Support for cryptocurrencies and traditional fiat currencies" },
              { name: "Cloud Infrastructure", description: "Scalable cloud architecture for 99.9% uptime guarantee" }
            ].map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start space-x-4 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-2xl"
              >
                <Check className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    {tech.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {tech.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              Join Our Growing Community
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Start your investment journey with a platform trusted by thousands worldwide
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/auth/register"
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all transform hover:scale-105"
              >
                Start Investing
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white/50 hover:border-white text-white px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all backdrop-blur-sm"
              >
                Contact Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AboutUs